import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";
import { useEffect } from "react";

interface AuthState {
  user: User | null;
  session: Session | null;
}

export function useAuth() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['auth'],
    queryFn: async (): Promise<AuthState> => {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      // If there's an error or session is invalid, sign out to clear stale data
      if (error) {
        console.warn('Session error, signing out:', error.message);
        await supabase.auth.signOut();
        return { user: null, session: null };
      }
      
      return {
        user: session?.user ?? null,
        session: session,
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: false, // Don't retry on auth errors
  });

  // Listen for auth changes and update cache
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        queryClient.setQueryData(['auth'], {
          user: session?.user ?? null,
          session: session,
        });
        // Invalidate dependent queries when auth changes
        if (!session) {
          queryClient.invalidateQueries({ queryKey: ['user-profile'] });
          queryClient.invalidateQueries({ queryKey: ['training-plan'] });
          queryClient.invalidateQueries({ queryKey: ['feedback-status'] });
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [queryClient]);

  return {
    user: query.data?.user ?? null,
    session: query.data?.session ?? null,
    isLoading: query.isLoading,
    isAuthenticated: !!query.data?.user,
  };
}
